/* hom: hom-measurements.js — custom measurements and made to order (House of Maryam layer).
   Loaded with defer after pubsub.js, global.js and hom-base.js (snippets/hom-head.liquid).

   Markup
   - snippets/hom-body-measurements.liquid: the one shared form, a native <dialog> inside
     <template id="HomMeasureTemplate">. It is cloned into <body> on first use and opened with
     showModal() (top layer: above the header, the cart drawer, quick view and app pop-ups).
   - snippets/hom-measure-card.liquid: the product page card (block 'hom_measurements').

   Line item properties (the contract shared with the bag area)
   - visible: "Fit", "Units", "Height", "Heel height", "Chest / bust", "Waist", "Hips", "Shoulder",
     "Sleeve length", "Armhole", "Biceps", "Wrist", "Top / dress length", "Bottom length",
     "Modesty requests", "Measurement notes", "Measurements confirmed" ("Yes, 28 Sep 2026"),
     "Custom order" (the custom order terms the customer ticked, with the time: "Made to your measurements.
     Not returnable for fit, size or a change of mind. Agreed 6 Oct 2026, 14:32 UK time"; owner, 6 Oct 2026).
     All dates are UK dates (the shop's time zone), so they agree with each other on the order.
     Only filled fields are written. Values carry their unit ("36 in", "91.5 cm", "5 ft 6 in").
   - hidden: "_hom_m" = "1" on every line that carries measurements; "_hom_consent" =
     "Given 28 Sep 2026" only when modesty requests or notes were given; "_hom_m_removed" = date,
     written (with a visible "Measurements" = the removed note) when a customer removes them;
     "_hom_terms" = when the custom order tick was given (ISO time, UTC) and the device's time zone.

   API: window.HOM.measurements (every method returns a promise)
   It is set when this deferred file runs, after hom-cart.js: read it lazily (at render or click
   time), or wait for the document event 'hom:measurements:ready' {detail: {enabled}}.
   - openForProduct(form, options?)  open the form for a product form (element, or its id);
       options.required shows the Custom Size notice, options.opener gets focus back.
       Resolves { formId, properties } after saving, or null when closed without saving.
       Saving writes hidden properties[…] inputs into that form.
   - editLine(lineKey, options?)   edit the measurements of a bag line (prefilled from the line).
   - addToLine(lineKey, options?)  add measurements to a bag line (prefilled from this device
       when saved there). options.opener: the element that gets focus back (default: the
       focused element; pass it, because Safari does not focus clicked buttons).
       Both save with POST /cart/change.js {id: key, quantity, properties}: every other property
       (Avis _apo_* and the rest) and the quantity are kept. Resolve { key, previousKey,
       properties } (a line's key changes with its properties), or null when closed.
   - removeLine(lineKey) keep the line (owner q29) and rewrite its properties without the
       measurement keys, plus "_hom_m_removed" and "Measurements" = the removed note.
   After each bag change the result is checked with GET /cart.js, then the document event
   'hom:cart:changed' {detail: {key, previousKey}} is dispatched; the bag area re-renders on it.
   The dialog closes and returns focus to the opener before that event, so the bag can move
   focus to the re-rendered line itself. If the bag changed but the check failed, the event is
   still sent (key: null) at once. editLine/addToLine then keep the form open with a message, and
   a second Save goes to the line's new key (taken from the /cart/change.js reply); only when the
   line cannot be followed does the form close and the promise reject. removeLine rejects.
   If the browser closes the form during a save (Esc twice, a back gesture), editLine/addToLine
   wait for that save and still send the event. Also exposed: enabled, keys, visibleKeys,
   hasMeasurements(props).

   Product page
   - The card follows Zenith's variantChange (HOM.onVariantChange + HOM.fulfilmentState): shown
     for Custom Size and standard stitched sizes, hidden for Unstitched / ready to ship / none.
     Saved inputs stay in the form only while the card is shown.
   - Custom Size with the block's "require" setting: Add to bag waits for saved measurements
     with at least one body measurement (height or a tape measurement; heel height, fit,
     requests or notes alone are not enough). Clicks on the form's submit buttons (including the sticky bar and Avis's cloned button) and
     the form's submit event are stopped in the capture phase on window, before Zenith's
     ProductForm or any app sees them; the card shows the message and the form opens.

   Remember on this device (a tick in the form): localStorage 'hom:measurements:v1' holds only
   fit, units and the measurements — never modesty requests or notes. Every access is wrapped in
   try/catch; the form works without storage. */
(() => {
  const HOM = window.HOM;
  if (!HOM || HOM.measurements) return;

  /* ---------- Keys and fields ---------- */
  const KEYS = {
    fit: 'Fit',
    units: 'Units',
    height: 'Height',
    heel: 'Heel height',
    chest: 'Chest / bust',
    waist: 'Waist',
    hips: 'Hips',
    shoulder: 'Shoulder',
    sleeve: 'Sleeve length',
    armhole: 'Armhole',
    biceps: 'Biceps',
    wrist: 'Wrist',
    top: 'Top / dress length',
    bottom: 'Bottom length',
    modesty: 'Modesty requests',
    notes: 'Measurement notes',
    confirmed: 'Measurements confirmed',
    customOrder: 'Custom order',
  };
  const MARKER = '_hom_m';
  const CONSENT = '_hom_consent';
  const REMOVED = '_hom_m_removed';
  const TERMS = '_hom_terms';
  const REMOVED_NOTE_KEY = 'Measurements';
  const VISIBLE_KEYS = Object.values(KEYS);
  const MEASUREMENT_KEYS = [...VISIBLE_KEYS, MARKER, CONSENT, TERMS];
  const ALL_KEYS = [...MEASUREMENT_KEYS, REMOVED, REMOVED_NOTE_KEY];
  const STORAGE_KEY = 'hom:measurements:v1';

  /* Single-value fields. inMax: above this (form in inches) it looks like centimetres;
     cmMin: below this (form in centimetres) it looks like inches. Wide on purpose (children's
     and plus sizes); the hints are gentle and never block saving. */
  const NUMERIC = [
    { id: 'heel', inMax: 7, cmMin: 0 },
    { id: 'chest', inMax: 70, cmMin: 45 },
    { id: 'waist', inMax: 70, cmMin: 40 },
    { id: 'hips', inMax: 75, cmMin: 45 },
    { id: 'shoulder', inMax: 30, cmMin: 20 },
    { id: 'sleeve', inMax: 40, cmMin: 10 },
    { id: 'armhole', inMax: 36, cmMin: 20 },
    { id: 'biceps', inMax: 30, cmMin: 15 },
    { id: 'wrist', inMax: 14, cmMin: 10 },
    { id: 'top', inMax: 70, cmMin: 30 },
    { id: 'bottom', inMax: 55, cmMin: 30 },
  ];
  const NUMERIC_IDS = NUMERIC.map((field) => field.id);
  const HEIGHT_IDS = ['height_ft', 'height_in', 'height_cm'];
  const TEXT_IDS = ['modesty', 'notes'];
  const STORED_IDS = [...HEIGHT_IDS, ...NUMERIC_IDS];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const template = document.getElementById('HomMeasureTemplate');
  const enabled = Boolean(template);

  // Dates and times in UK time (the shop's time), so every date on an order agrees whatever the customer's clock
  // says: "6 Oct 2026" and "6 Oct 2026, 14:32 UK time". UTC if the browser has no time zone data.
  const pad2 = (n) => String(n).padStart(2, '0');
  const ukParts = (d) => {
    try {
      const parts = {};
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', day: 'numeric', month: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(d).forEach((part) => { parts[part.type] = part.value; });
      return { day: Number(parts.day), month: Number(parts.month) - 1, year: parts.year, time: `${parts.hour}:${parts.minute}`, zone: 'UK time' };
    } catch (error) {
      return { day: d.getUTCDate(), month: d.getUTCMonth(), year: d.getUTCFullYear(), time: `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`, zone: 'UTC' };
    }
  };
  const today = () => {
    const t = ukParts(new Date());
    return `${t.day} ${MONTHS[t.month]} ${t.year}`;
  };
  const stampUk = (d) => {
    const t = ukParts(d);
    return `${t.day} ${MONTHS[t.month]} ${t.year}, ${t.time} ${t.zone}`;
  };
  const deviceTimeZone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (error) {
      return '';
    }
  };

  const hasMeasurements = (props) =>
    Boolean(props) && (props[MARKER] === '1' || VISIBLE_KEYS.some((key) => key !== KEYS.units && props[key]));

  /* Custom Size needs at least one body measurement (height or a tape measurement, not heel height). */
  const BODY_KEYS = [KEYS.height, KEYS.chest, KEYS.waist, KEYS.hips, KEYS.shoulder, KEYS.sleeve, KEYS.armhole,
    KEYS.biceps, KEYS.wrist, KEYS.top, KEYS.bottom];
  const hasBodyMeasurement = (props) => Boolean(props) && BODY_KEYS.some((key) => props[key]);

  const withoutMeasurementKeys = (props) => {
    const kept = {};
    Object.keys(props || {}).forEach((key) => {
      if (!ALL_KEYS.includes(key)) kept[key] = props[key];
    });
    return kept;
  };

  /* ---------- Numbers ---------- */
  const parseNumber = (raw) => {
    const text = String(raw == null ? '' : raw)
      .trim()
      .replace(',', '.')
      .replace(/\s*(inches|inch|in|centimetres|centimeters|cm|feet|foot|ft|"|”|″|')\s*$/i, '')
      .trim();
    if (text === '') return null;
    if (!/^\d*\.?\d+$/.test(text) && !/^\d+\.$/.test(text)) return NaN;
    return Number(text);
  };
  const tidy = (n, step) => {
    const rounded = Math.round(n / step) * step;
    return String(Math.round(rounded * 100) / 100);
  };
  const toCm = (n) => tidy(n * 2.54, 0.5);
  const toIn = (n) => tidy(n / 2.54, 0.25);

  /* ---------- Storage (per-device, opt-in) ---------- */
  const loadStored = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || data.v !== 1 || typeof data.values !== 'object') return null;
      return data;
    } catch (error) {
      return null;
    }
  };
  const store = (values) => {
    const data = { v: 1, units: values.units, fit: values.fit || '', values: {} };
    STORED_IDS.forEach((id) => {
      if (values[id]) data.values[id] = values[id];
    });
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return true;
    } catch (error) {
      return false;
    }
  };
  const forgetStored = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* storage blocked: nothing to forget */
    }
  };
  const valuesFromStored = (data) => {
    const values = { units: data.units === 'cm' ? 'cm' : 'in', fit: ['regular', 'loose'].includes(data.fit) ? data.fit : '' };
    STORED_IDS.forEach((id) => {
      const n = parseNumber(data.values[id]);
      if (n != null && !Number.isNaN(n)) values[id] = String(n);
    });
    return values;
  };

  /* ---------- Config ---------- */
  let config = { defaultUnit: 'in', removedNote: 'Removed. Our team will contact you about sizing', strings: {} };
  if (template) {
    const script = template.content.querySelector('[data-hom-measure-config]');
    try {
      config = Object.assign(config, JSON.parse(script ? script.textContent : '{}'));
    } catch (error) {
      /* keep the defaults */
    }
  }
  const S = config.strings || {};
  const say = (name, fallback, vars = {}) =>
    Object.keys(vars).reduce((text, key) => text.split(`[${key}]`).join(vars[key]), S[name] || fallback || '');
  const unitName = (unit) => (unit === 'cm' ? say('centimetres', 'Centimetres') : say('inches', 'Inches'));

  /* ---------- Properties <-> form values ---------- */
  const buildProps = (values) => {
    const unit = values.units === 'cm' ? 'cm' : 'in';
    const date = today();
    const props = {};
    const measures = {};

    // First, so it leads the line in the bag, at checkout and on the order.
    if (values.agree) {
      const now = new Date();
      const note = say('customOrder', 'Made to your measurements. Not returnable for fit, size or a change of mind.');
      props[KEYS.customOrder] = `${note} ${say('customOrderAgreed', 'Agreed [when]', { when: stampUk(now) })}`.trim();
      props[TERMS] = `${now.toISOString()} ${deviceTimeZone()}`.trim();
    }

    if (unit === 'in') {
      const ft = parseNumber(values.height_ft);
      const inch = parseNumber(values.height_in);
      if (ft) measures[KEYS.height] = inch ? `${tidy(ft, 0.01)} ft ${tidy(inch, 0.25)} in` : `${tidy(ft, 0.01)} ft`;
    } else {
      const cm = parseNumber(values.height_cm);
      if (cm) measures[KEYS.height] = `${tidy(cm, 0.5)} cm`;
    }
    NUMERIC_IDS.forEach((id) => {
      const n = parseNumber(values[id]);
      if (n) measures[KEYS[id]] = `${tidy(n, 0.01)} ${unit}`;
    });

    if (values.fit) props[KEYS.fit] = values.fit === 'loose' ? say('fitLoose', 'Loose') : say('fitRegular', 'Regular');
    if (Object.keys(measures).length) props[KEYS.units] = unitName(unit);
    Object.assign(props, measures);

    const modesty = (values.modesty || '').trim();
    const notes = (values.notes || '').trim();
    if (modesty) props[KEYS.modesty] = modesty;
    if (notes) props[KEYS.notes] = notes;

    props[KEYS.confirmed] = `Yes, ${date}`;
    props[MARKER] = '1';
    if (modesty || notes) props[CONSENT] = `Given ${date}`;
    return props;
  };

  const valuesFromProps = (props) => {
    const values = { units: '', fit: '' };
    const read = (key) => (props[key] == null ? '' : String(props[key]));

    const units = read(KEYS.units).toLowerCase();
    if (units.startsWith('c')) values.units = 'cm';
    else if (units.startsWith('i')) values.units = 'in';
    else {
      const sample = VISIBLE_KEYS.map(read).join(' ');
      values.units = /\dcm\b|\d\s+cm\b/i.test(sample) ? 'cm' : 'in';
    }

    const fit = read(KEYS.fit).toLowerCase();
    if (fit.startsWith('loose')) values.fit = 'loose';
    else if (fit.startsWith('regular')) values.fit = 'regular';

    const height = read(KEYS.height);
    const ftMatch = height.match(/(\d+(?:\.\d+)?)\s*ft(?:\s*(\d+(?:\.\d+)?)\s*in)?/i);
    const cmMatch = height.match(/(\d+(?:\.\d+)?)\s*cm/i);
    if (ftMatch) {
      if (values.units === 'cm') {
        values.height_cm = tidy((Number(ftMatch[1]) * 12 + Number(ftMatch[2] || 0)) * 2.54, 1);
      } else {
        values.height_ft = ftMatch[1];
        if (ftMatch[2]) values.height_in = ftMatch[2];
      }
    } else if (cmMatch) {
      if (values.units === 'in') {
        const total = Number(cmMatch[1]) / 2.54;
        values.height_ft = String(Math.floor(total / 12));
        values.height_in = tidy(total - Math.floor(total / 12) * 12, 1);
      } else {
        values.height_cm = cmMatch[1];
      }
    }

    NUMERIC_IDS.forEach((id) => {
      const match = read(KEYS[id]).match(/(\d+(?:\.\d+)?)/);
      if (match) values[id] = match[1];
    });
    values.modesty = read(KEYS.modesty);
    values.notes = read(KEYS.notes);
    values.consent = Boolean(props[CONSENT]) && Boolean(values.modesty || values.notes);
    return values;
  };

  const summarize = (props) => {
    const parts = [];
    if (props[KEYS.fit]) parts.push(`${KEYS.fit} ${String(props[KEYS.fit]).toLowerCase()}`);
    [KEYS.height, ...NUMERIC_IDS.map((id) => KEYS[id])].forEach((key) => {
      if (props[key]) parts.push(`${key} ${props[key]}`);
    });
    [KEYS.modesty, KEYS.notes].forEach((key) => {
      if (props[key]) parts.push(`${key} ${say('added', 'added')}`);
    });
    if (parts.length <= 3) return parts.join(' · ');
    return `${parts.slice(0, 3).join(' · ')} · ${say('more', '+[n] more', { n: parts.length - 3 })}`;
  };

  /* ---------- Dialog ---------- */
  let dialog = null;
  let ui = null;
  let session = null;
  let swallowEscapeUntil = 0;

  const ensureDialog = () => {
    if (dialog) return dialog;
    if (!template) return null;
    const fragment = template.content.cloneNode(true);
    dialog = fragment.querySelector('[data-hom-measure-dialog]');
    document.body.appendChild(fragment);
    const $ = (selector) => dialog.querySelector(selector);
    ui = {
      form: $('[data-hom-measure-form]'),
      body: $('[data-hom-measure-body]'),
      title: $('#HomMeasureTitle'),
      context: $('[data-hom-measure-context]'),
      required: $('[data-hom-measure-required]'),
      prefilled: $('[data-hom-measure-prefilled]'),
      unitHint: $('[data-hom-measure-unit-hint]'),
      unitHintText: $('[data-hom-measure-unit-hint-text]'),
      reinterpret: $('[data-hom-measure-reinterpret]'),
      consent: $('[data-hom-measure-consent]'),
      consentBox: $('#HomMeasure-consent'),
      agreeBox: $('#HomMeasure-agree'),
      remember: $('#HomMeasure-remember'),
      forget: $('[data-hom-measure-forget]'),
      errors: $('[data-hom-measure-errors]'),
      save: $('[data-hom-measure-save]'),
      status: $('[data-hom-measure-status]'),
    };
    bindDialog();
    return dialog;
  };

  const input = (id) => dialog.querySelector(`[data-hom-input="${id}"]`);
  const msgFor = (field) => dialog.querySelector(`[data-hom-msg="${field}"]`);
  const fieldOf = (id) => (HEIGHT_IDS.includes(id) ? 'height' : id);

  const currentUnits = () => {
    const checked = dialog.querySelector('input[name="units"]:checked');
    return checked && checked.value === 'cm' ? 'cm' : 'in';
  };

  const readForm = () => {
    const fit = dialog.querySelector('input[name="fit"]:checked');
    const values = { units: currentUnits(), fit: fit ? fit.value : '' };
    [...STORED_IDS, ...TEXT_IDS].forEach((id) => {
      const el = input(id);
      values[id] = el ? el.value.trim() : '';
    });
    values.consent = Boolean(ui.consentBox && ui.consentBox.checked);
    values.agreeRequired = Boolean(ui.agreeBox);
    values.agree = Boolean(ui.agreeBox && ui.agreeBox.checked);
    return values;
  };

  const announce = (text) => {
    if (!ui || !ui.status) return;
    ui.status.textContent = '';
    window.setTimeout(() => {
      ui.status.textContent = text;
    }, 60);
  };

  const showUnits = (unit) => {
    dialog.querySelectorAll('[data-hom-units-radio]').forEach((radio) => {
      radio.checked = radio.value === unit;
    });
    dialog.querySelectorAll('[data-hom-unit]').forEach((el) => {
      el.textContent = unit;
    });
    dialog.querySelectorAll('[data-hom-unit-long]').forEach((el) => {
      el.textContent = `, ${unit === 'cm' ? say('cmLong', 'in centimetres') : say('inLong', 'in inches')}`;
    });
    dialog.querySelectorAll('[data-hom-height-units]').forEach((el) => {
      el.hidden = el.dataset.homHeightUnits !== unit;
    });
    dialog.dataset.units = unit;
  };

  const setMessage = (field, text, kind) => {
    const el = msgFor(field);
    if (!el) return;
    el.textContent = text || '';
    el.classList.toggle('is-error', kind === 'error' && Boolean(text));
    el.classList.toggle('is-hint', kind === 'hint' && Boolean(text));
  };

  const setInvalid = (id, invalid) => {
    const el = input(id);
    if (!el) return;
    if (invalid) el.setAttribute('aria-invalid', 'true');
    else el.removeAttribute('aria-invalid');
  };

  /** Checks one field. Returns { error, hint, hintUnit } (hintUnit: the unit the value looks like). */
  const checkField = (field, values) => {
    const unit = values.units;
    if (field === 'height') {
      if (unit === 'cm') {
        const cm = parseNumber(values.height_cm);
        if (cm == null) return {};
        if (Number.isNaN(cm) || cm <= 0) return { error: say('notNumber'), ids: ['height_cm'] };
        if (cm > 250) return { error: say('tooLarge'), ids: ['height_cm'] };
        if (cm < 90) return { hint: say('lookIn'), hintUnit: 'in' };
        return {};
      }
      const ft = parseNumber(values.height_ft);
      const inch = parseNumber(values.height_in);
      if (ft == null && inch == null) return {};
      if (Number.isNaN(ft) || (ft != null && ft <= 0)) return { error: say('notNumber'), ids: ['height_ft'] };
      if (Number.isNaN(inch) || (inch != null && inch < 0)) return { error: say('notNumber'), ids: ['height_in'] };
      if (ft == null) return { error: say('heightFeet'), ids: ['height_ft'] };
      if (inch != null && inch >= 12) return { error: say('heightInches'), ids: ['height_in'] };
      if (ft > 250) return { error: say('tooLarge'), ids: ['height_ft'] };
      if (ft >= 90) return { hint: say('lookCm'), hintUnit: 'cm' };
      // Nobody is over 8 ft: 9 to 89 is most likely a height in inches typed into the feet box.
      if (ft > 8) return { error: say('heightFormat'), ids: ['height_ft'] };
      return {};
    }
    const spec = NUMERIC.find((item) => item.id === field);
    if (!spec) return {};
    const n = parseNumber(values[field]);
    if (n == null) return {};
    if (Number.isNaN(n) || n <= 0) return { error: say('notNumber'), ids: [field] };
    if ((unit === 'in' && n > 120) || (unit === 'cm' && n > 300)) return { error: say('tooLarge'), ids: [field] };
    if (unit === 'in' && n > spec.inMax) return { hint: say('lookCm'), hintUnit: 'cm' };
    if (unit === 'cm' && spec.cmMin && n < spec.cmMin) return { hint: say('lookIn'), hintUnit: 'in' };
    return {};
  };

  /** Updates messages for the given fields (all when omitted). Returns the results by field. */
  const refreshChecks = (fields, { showErrors = false } = {}) => {
    const values = readForm();
    const list = fields || ['height', ...NUMERIC_IDS];
    const results = {};
    list.forEach((field) => {
      const result = checkField(field, values);
      results[field] = result;
      const ids = field === 'height' ? HEIGHT_IDS : [field];
      ids.forEach((id) => setInvalid(id, false));
      if (result.error && showErrors) {
        (result.ids || ids).forEach((id) => setInvalid(id, true));
        setMessage(field, result.error, 'error');
      } else if (result.error) {
        // Not shown while typing; only a stale message is cleared.
        const el = msgFor(field);
        if (el && el.classList.contains('is-hint')) setMessage(field, '', '');
      } else if (result.hint) {
        setMessage(field, result.hint, 'hint');
      } else {
        setMessage(field, '', '');
      }
    });
    updateUnitHint();
    return results;
  };

  const updateUnitHint = () => {
    if (!ui.unitHint) return;
    const values = readForm();
    const other = values.units === 'in' ? 'cm' : 'in';
    const looksOther = ['height', ...NUMERIC_IDS].some((field) => checkField(field, values).hintUnit === other);
    ui.unitHint.hidden = !looksOther;
    if (looksOther) {
      ui.unitHintText.textContent = other === 'cm' ? say('unitHintCm') : say('unitHintIn');
      ui.reinterpret.textContent = other === 'cm' ? say('reinterpretCm') : say('reinterpretIn');
      ui.reinterpret.dataset.unit = other;
    }
  };

  const updateConsent = () => {
    const hasText = TEXT_IDS.some((id) => input(id) && input(id).value.trim() !== '');
    ui.consent.hidden = !hasText;
    if (!hasText) {
      ui.consentBox.checked = false;
      setMessage('consent', '', '');
    }
  };

  const updateForget = () => {
    if (ui.forget) ui.forget.hidden = !loadStored();
  };

  const clearErrors = () => {
    ui.errors.textContent = '';
    dialog.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute('aria-invalid'));
    dialog.querySelectorAll('[data-hom-msg].is-error').forEach((el) => {
      el.textContent = '';
      el.classList.remove('is-error');
    });
  };

  const fillForm = (values) => {
    const v = values || {};
    ui.form.reset();
    showUnits(v.units === 'cm' || v.units === 'in' ? v.units : config.defaultUnit === 'cm' ? 'cm' : 'in');
    dialog.querySelectorAll('input[name="fit"]').forEach((radio) => {
      radio.checked = radio.value === v.fit;
    });
    [...STORED_IDS, ...TEXT_IDS].forEach((id) => {
      const el = input(id);
      if (el) el.value = v[id] == null ? '' : String(v[id]);
    });
    dialog.querySelectorAll('[data-hom-msg]').forEach((el) => {
      el.textContent = '';
      el.classList.remove('is-error', 'is-hint');
    });
    clearErrors();
    updateConsent();
    if (ui.consentBox) ui.consentBox.checked = Boolean(v.consent) && !ui.consent.hidden;
    // The custom order terms are agreed afresh on every save, so the tick always starts empty.
    if (ui.agreeBox) ui.agreeBox.checked = false;
    const stored = loadStored();
    ui.remember.checked = Boolean(stored);
    updateForget();
    refreshChecks();
  };

  /** Switch units; with convert, every number is converted to the new unit. */
  const switchUnits = (unit, convert) => {
    const values = readForm();
    const from = dialog.dataset.units === 'cm' ? 'cm' : 'in';
    if (from === unit) return;
    let heightConverted = false; // reinterpreting: a real height was converted, not kept as typed
    if (convert) {
      NUMERIC_IDS.forEach((id) => {
        const n = parseNumber(values[id]);
        if (n && !Number.isNaN(n)) input(id).value = unit === 'cm' ? toCm(n) : toIn(n);
      });
      if (unit === 'cm') {
        const ft = parseNumber(values.height_ft);
        const inch = parseNumber(values.height_in);
        if (ft && !Number.isNaN(ft)) {
          input('height_cm').value = tidy((ft * 12 + (inch && !Number.isNaN(inch) ? inch : 0)) * 2.54, 1);
        }
        input('height_ft').value = '';
        input('height_in').value = '';
      } else {
        const cm = parseNumber(values.height_cm);
        if (cm && !Number.isNaN(cm)) {
          const total = Math.round(cm / 2.54);
          input('height_ft').value = String(Math.floor(total / 12));
          input('height_in').value = String(total % 12);
        }
        input('height_cm').value = '';
      }
    } else if (unit === 'cm') {
      // Reinterpret: a number of 90 or more in the feet box was really centimetres. Feet and
      // inches are unambiguous (two boxes), so a real feet-and-inches height is converted.
      const ft = parseNumber(values.height_ft);
      const inch = parseNumber(values.height_in);
      if (ft && !Number.isNaN(ft)) {
        heightConverted = ft < 90;
        input('height_cm').value =
          ft >= 90 ? values.height_ft : tidy((ft * 12 + (inch && !Number.isNaN(inch) ? inch : 0)) * 2.54, 1);
      }
      input('height_ft').value = '';
      input('height_in').value = '';
    } else {
      const cm = parseNumber(values.height_cm);
      if (cm && !Number.isNaN(cm)) {
        // Under 90 "cm" was most likely total inches; otherwise it is a real height in centimetres.
        heightConverted = cm >= 90;
        const total = cm < 90 ? cm : cm / 2.54;
        const feet = Math.floor(Math.round(total) / 12);
        input('height_ft').value = String(feet);
        input('height_in').value = String(Math.round(total) - feet * 12);
      }
      input('height_cm').value = '';
    }
    showUnits(unit);
    if (session) session.unitsAcknowledged = false;
    refreshChecks();
    const name = unitName(unit).toLowerCase();
    let message = 'converted';
    if (!convert) message = heightConverted ? 'reinterpretedHeight' : 'reinterpreted';
    announce(say(message, '', { unit: name }));
  };

  const validate = (values, custom) => {
    const errors = [];
    const hints = [];
    ['height', ...NUMERIC_IDS].forEach((field) => {
      const result = checkField(field, values);
      if (result.error) errors.push({ field, ids: result.ids || [field], message: result.error });
      else if (result.hintUnit) hints.push(field);
    });

    const hasMeasure =
      (values.units === 'cm' ? parseNumber(values.height_cm) > 0 : parseNumber(values.height_ft) > 0) ||
      NUMERIC_IDS.filter((id) => id !== 'heel').some((id) => parseNumber(values[id]) > 0);
    const hasText = TEXT_IDS.some((id) => values[id]);
    const hasAny = hasMeasure || hasText || Boolean(values.fit) || parseNumber(values.heel) > 0;

    let summary = '';
    if (!errors.length && custom && !hasMeasure) summary = say('needOne');
    else if (!errors.length && !hasAny) summary = say('needAny');

    let consentError = '';
    if (hasText && !values.consent) consentError = say('consent');

    let agreeError = '';
    if (values.agreeRequired && !values.agree) agreeError = say('agree');

    return { errors, hints, summary, consentError, agreeError };
  };

  const focusFirst = (selectors) => {
    for (const selector of selectors) {
      const el = selector && dialog.querySelector(selector);
      if (el && !el.closest('[hidden]')) {
        el.focus();
        return;
      }
    }
  };

  const setBusy = (busy) => {
    if (!ui.save) return;
    ui.save.classList.toggle('loading', busy);
    ui.save.setAttribute('aria-disabled', String(busy));
    const spinner = ui.save.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !busy);
    dialog.setAttribute('aria-busy', String(busy));
  };

  /* The footer message (role="alert"): emptied first, so a repeated message is announced again. */
  const showSummary = (text) => {
    ui.errors.textContent = '';
    window.setTimeout(() => {
      ui.errors.textContent = text;
    }, 40);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!session || session.busy) return;
    clearErrors();
    const values = readForm();
    const result = validate(values, session.custom);

    if (result.errors.length) {
      result.errors.forEach((error) => {
        error.ids.forEach((id) => setInvalid(id, true));
        setMessage(error.field, error.message, 'error');
      });
      showSummary(say('fixErrors'));
      focusFirst(result.errors[0].ids.map((id) => `[data-hom-input="${id}"]`));
      return;
    }
    if (result.summary) {
      showSummary(result.summary);
      focusFirst(['[data-hom-input="chest"]']);
      return;
    }
    if (result.consentError) {
      setMessage('consent', result.consentError, 'error');
      ui.consentBox.setAttribute('aria-invalid', 'true');
      showSummary(result.consentError);
      ui.consentBox.focus();
      return;
    }
    if (result.agreeError) {
      setMessage('agree', result.agreeError, 'error');
      ui.agreeBox.setAttribute('aria-invalid', 'true');
      showSummary(result.agreeError);
      ui.agreeBox.focus();
      return;
    }
    if (result.hints.length && !session.unitsAcknowledged) {
      session.unitsAcknowledged = true;
      showSummary(say('checkUnits'));
      focusFirst(
        result.hints.map((field) =>
          field === 'height'
            ? `[data-hom-input="${values.units === 'cm' ? 'height_cm' : 'height_ft'}"]`
            : `[data-hom-input="${field}"]`
        )
      );
      return;
    }

    const props = buildProps(values);
    // The browser can still close the form while the bag is updated (Esc twice, a back gesture),
    // and the form may even be open for something else by the time the save ends.
    const current = session;
    const remember = ui.remember.checked;
    current.busy = true;
    setBusy(true);
    try {
      const outcome = await current.onSave(values, props);
      if (remember) store(values);
      else forgetStored();
      if (session === current) finish(outcome, { saved: true, values });
      else announceOutside(say('saved'));
    } catch (error) {
      current.busy = false;
      if (session === current) showSummary(say('cartError'));
      else announceOutside(say('cartError'));
    } finally {
      if (!session || session === current) setBusy(false);
    }
  };

  const bindDialog = () => {
    ui.form.addEventListener('submit', onSubmit);

    // No close on a backdrop click: a stray tap must not throw away a half-filled form.
    dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (target === dialog) return;
      if (target.closest('[data-hom-measure-close], [data-hom-measure-cancel]')) {
        requestClose();
      } else if (target.closest('[data-hom-measure-reinterpret]')) {
        const unit = ui.reinterpret.dataset.unit === 'cm' ? 'cm' : 'in';
        switchUnits(unit, false);
        const radio = dialog.querySelector(`[data-hom-units-radio][value="${unit}"]`);
        if (radio) radio.focus();
      } else if (target.closest('[data-hom-measure-forget]')) {
        forgetStored();
        ui.remember.checked = false;
        updateForget();
        announce(say('forgotten'));
        ui.remember.focus();
      } else if (target.closest('[data-hom-measure-clear]')) {
        const units = currentUnits();
        fillForm({ units });
        ui.prefilled.hidden = true;
        announce(say('cleared'));
      }
    });

    dialog.addEventListener('change', (event) => {
      const target = event.target;
      if (target.matches('[data-hom-units-radio]')) {
        switchUnits(target.value === 'cm' ? 'cm' : 'in', true);
      } else if (target.matches('input[data-hom-input]')) {
        // On leaving a changed field: its error or "other unit" hint shows straight away.
        refreshChecks([fieldOf(target.dataset.homInput)], { showErrors: true });
        if (session) session.unitsAcknowledged = false;
      } else if (target === ui.consentBox && ui.consentBox.checked) {
        ui.consentBox.removeAttribute('aria-invalid');
        setMessage('consent', '', '');
      } else if (target === ui.agreeBox && ui.agreeBox.checked) {
        ui.agreeBox.removeAttribute('aria-invalid');
        setMessage('agree', '', '');
      }
    });

    dialog.addEventListener('input', (event) => {
      const target = event.target;
      if (target.matches('textarea[data-hom-input]')) updateConsent();
      else if (target.matches('[data-hom-input]') && target.getAttribute('aria-invalid') === 'true') {
        // Clear an error as soon as the value becomes valid.
        const field = fieldOf(target.dataset.homInput);
        if (!checkField(field, readForm()).error) {
          refreshChecks([field]);
          if (!dialog.querySelector('[aria-invalid="true"]')) ui.errors.textContent = '';
        }
      }
    });

    // Esc: close ourselves (focus return), and swallow the matching keyup so drawers and
    // quick view (which close on keyup Escape) don't close too once focus is back in them.
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      swallowEscapeUntil = Date.now() + 800;
      requestClose();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && typeof dialog.showModal !== 'function') {
        swallowEscapeUntil = Date.now() + 800;
        requestClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => !el.disabled && !el.closest('[hidden]') && el.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === ui.title)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    // Closed by the browser itself (e.g. a phone's back gesture). The event is queued, so a
    // form that was re-opened in the meantime is left alone.
    dialog.addEventListener('close', () => {
      if (session && !dialog.open) close();
    });
  };

  window.addEventListener(
    'keyup',
    (event) => {
      if (event.key !== 'Escape' || Date.now() > swallowEscapeUntil) return;
      swallowEscapeUntil = 0;
      event.stopImmediatePropagation();
    },
    true
  );

  const lockPage = (lock) => document.documentElement.classList.toggle('hom-measure-lock', lock);

  /**
   * Opens the form. Resolves with what onSave returned, or null when closed without saving.
   * @param {{mode: string, custom: boolean, required?: boolean, requiredText?: string, values?: object,
   *   prefilled?: boolean, context?: string, opener?: Element, focusAfter?: (session: object) => Element|null,
   *   announceRegion?: Element, onCancel?: (draft: object) => void,
   *   onSave: (values: object, props: object) => any}} options
   *   custom: at least one body measurement is needed to save (Custom Size).
   */
  const openDialog = (options) => {
    if (!ensureDialog()) return Promise.resolve(null);
    if (session) close();

    return new Promise((resolve) => {
      session = Object.assign({ resolve, unitsAcknowledged: false, busy: false }, options);
      fillForm(options.values);
      setBusy(false); // a save from a form the browser closed may still be running
      ui.prefilled.hidden = !options.prefilled;
      ui.context.textContent = options.context || '';
      ui.context.hidden = !options.context;
      ui.required.textContent = options.required ? options.requiredText || say('required') : '';
      ui.required.hidden = !options.required;
      if (options.required) dialog.setAttribute('aria-describedby', 'HomMeasureRequired');
      else dialog.removeAttribute('aria-describedby');

      lockPage(true);
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        dialog.classList.add('hom-measure--fallback');
      }
      // Once shown (a hidden element keeps its old scroll position): start at the top.
      ui.body.scrollTop = 0;
      ui.title.focus({ preventScroll: true });
    });
  };

  /* Closing by the customer (Close, Cancel, Esc): not while the bag is being updated.
     A product form's unsaved entries are kept as a draft for the next time it opens. */
  const requestClose = () => {
    if (!session || session.busy) return;
    if (typeof session.onCancel === 'function') session.onCancel(readForm());
    close();
  };

  const close = () => {
    const current = session;
    if (!current) return;
    session = null;
    if (dialog.open && typeof dialog.close === 'function' && typeof dialog.showModal === 'function') dialog.close();
    else dialog.removeAttribute('open');
    lockPage(false);
    returnFocus(current);
    current.resolve(current.result === undefined ? null : current.result);
  };

  const isShown = (el) => el && el.isConnected && !el.closest('[hidden]') && el.getClientRects().length > 0;

  const returnFocus = (current) => {
    let target = current.focusAfter ? current.focusAfter(current) : null;
    if (!isShown(target)) target = current.opener;
    if (!isShown(target)) return;
    target.focus({ preventScroll: true });
    // Zenith drawers keep focus with trapFocus(); re-arm it for the drawer we return to.
    const container = target.closest('cart-drawer, quick-view-modal, modal-dialog');
    if (container && typeof trapFocus === 'function' && container.hasAttribute('open')) {
      const panel =
        container.tagName === 'CART-DRAWER'
          ? document.getElementById('CartDrawer') || container
          : container.querySelector('[role="dialog"]') || container;
      trapFocus(panel, target);
    }
  };

  const finish = (outcome, info) => {
    if (!session) return;
    session.result = outcome;
    session.saved = info.saved;
    const region = session.announceRegion && session.announceRegion.isConnected ? session.announceRegion : null;
    close();
    announceOutside(say('saved'), region);
  };

  /* A polite message for the page (the dialog's own live region is gone once it closes). */
  let pageStatus = null;
  const announceOutside = (text, region) => {
    const el =
      region ||
      pageStatus ||
      (() => {
        pageStatus = document.createElement('div');
        pageStatus.className = 'hom-visually-hidden';
        pageStatus.setAttribute('aria-live', 'polite');
        document.body.appendChild(pageStatus);
        return pageStatus;
      })();
    el.textContent = '';
    window.setTimeout(() => {
      el.textContent = text;
    }, 120);
  };

  /* ---------- Product page ---------- */
  const entries = new Map(); // "<form id>::<product id>" -> { values, props, saved }

  const cardForForm = (formId) =>
    Array.from(document.querySelectorAll('[data-hom-measure-card]')).find((card) => card.dataset.formId === formId) ||
    null;
  const entryKey = (formId, card) => `${formId}::${(card && card.dataset.productId) || ''}`;
  const isActiveState = (state) => state === 'custom' || state === 'mto';
  const flagsOf = (card) => ({
    readyToDispatch: card.dataset.readyToDispatch === 'true',
    hasSizeOption: card.dataset.hasSizeOption === 'true',
    unstitchedProduct: card.dataset.unstitchedProduct === 'true',
  });

  const mainFormFor = (form) => {
    const id = (form && form.getAttribute('id')) || '';
    if (id.startsWith('sticky-bar-product-form-')) {
      return document.getElementById(`product-form-${id.slice('sticky-bar-product-form-'.length)}`) || form;
    }
    return form;
  };

  const syncInputs = (formId) => {
    const form = document.getElementById(formId);
    if (!form || form.tagName !== 'FORM') return;
    const card = cardForForm(formId);
    const entry = entries.get(entryKey(formId, card));
    const active = Boolean(entry && entry.saved) && (!card || isActiveState(card.dataset.state));
    let box = form.querySelector('[data-hom-measure-inputs]');
    if (!active) {
      if (box) box.remove();
      return;
    }
    if (!box) {
      box = document.createElement('div');
      box.hidden = true;
      box.setAttribute('data-hom-measure-inputs', '');
      form.appendChild(box);
    }
    box.replaceChildren(
      ...Object.keys(entry.props).map((key) => {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = `properties[${key}]`;
        hidden.value = entry.props[key];
        return hidden;
      })
    );
  };

  /* Saved, and enough for the selected size: Custom Size needs a body measurement (a set saved
     on a standard size with only requests or notes is not enough once Custom Size is chosen). */
  const isComplete = (entry, custom) => Boolean(entry && entry.saved) && (!custom || hasBodyMeasurement(entry.props));

  const renderCard = (card) => {
    const state = card.dataset.state;
    const active = isActiveState(state);
    const entry = entries.get(entryKey(card.dataset.formId, card));
    const custom = state === 'custom';
    const saved = isComplete(entry, custom);
    card.hidden = !active;
    card.classList.toggle('hom-measure-card--custom', custom);
    card.classList.toggle('hom-measure-card--saved', saved);
    card.querySelectorAll('[data-hom-card-part]').forEach((part) => {
      const kind = part.dataset.homCardPart;
      part.hidden = saved ? kind !== 'saved' : kind === 'saved' || (kind === 'custom') !== custom;
    });
    const summary = card.querySelector('[data-hom-measure-summary]');
    if (summary) summary.textContent = saved ? summarize(entry.props) : '';
    const add = card.querySelector('[data-hom-measure-add]');
    const edit = card.querySelector('[data-hom-measure-edit]');
    const remove = card.querySelector('[data-hom-measure-remove]');
    if (add) add.hidden = saved;
    if (edit) edit.hidden = !saved;
    if (remove) remove.hidden = !saved;
    if (saved || !custom) setAlert(card, '');
  };

  const setAlert = (card, text) => {
    const alert = card.querySelector('[data-hom-measure-alert]');
    if (!alert) return;
    if (!text) {
      alert.textContent = '';
      return;
    }
    alert.textContent = '';
    window.setTimeout(() => {
      alert.textContent = text;
    }, 30);
  };

  const initCard = (card) => {
    renderCard(card);
    syncInputs(card.dataset.formId);
  };

  const productContext = (card) => {
    if (!card) return '';
    return [card.dataset.productTitle, card.dataset.variantTitle].filter(Boolean).join(' · ');
  };

  const openForProduct = (formOrId, options = {}) => {
    if (!enabled) return Promise.resolve(null);
    const given = typeof formOrId === 'string' ? document.getElementById(formOrId) : formOrId;
    const form = mainFormFor(given);
    const formId = form && form.getAttribute('id');
    if (!formId) return Promise.reject(new Error('hom-measurements: product form not found'));
    const card = cardForForm(formId);
    const key = entryKey(formId, card);
    const entry = entries.get(key);
    const custom = Boolean(options.required) || (card ? card.dataset.state === 'custom' : false);

    // Saved values first, then an unsaved draft from this page, then this device's saved set.
    let values = entry && entry.values ? entry.values : null;
    let prefilled = false;
    if (!values) {
      const stored = loadStored();
      if (stored) {
        values = valuesFromStored(stored);
        prefilled = true;
      }
    }

    return openDialog({
      mode: 'product',
      onCancel: (draft) => {
        const current = entries.get(key);
        if (current && current.saved) return; // Cancel keeps what was saved
        // An empty form leaves no draft, so the device's saved set fills it in next time.
        const typed = Boolean(draft.fit) || [...STORED_IDS, ...TEXT_IDS].some((id) => draft[id]);
        if (typed) entries.set(key, { values: draft, props: null, saved: false });
        else entries.delete(key);
      },
      custom,
      required: Boolean(options.required),
      requiredText: card && card.dataset.requiredMessage,
      values,
      prefilled,
      context: productContext(card),
      opener: options.opener || document.activeElement,
      focusAfter: (current) => {
        if (!current.saved || !card) return null;
        // Opened from Add to bag: back to Add to bag. From the card: its Edit button.
        if (current.required && isShown(current.opener)) return current.opener;
        return card.querySelector('[data-hom-measure-edit]');
      },
      onSave: (formValues, props) => {
        const values = Object.assign({}, formValues);
        entries.set(key, { values, props, saved: true });
        if (card) renderCard(card);
        syncInputs(formId);
        return { formId, properties: Object.assign({}, props) };
      },
      announceRegion: card && card.querySelector('[data-hom-measure-status]'),
    });
  };

  const removeFromForm = (card) => {
    const formId = card.dataset.formId;
    const entry = entries.get(entryKey(formId, card));
    if (entry) entry.saved = false; // the values stay as a draft for the next "Add"
    renderCard(card);
    syncInputs(formId);
    announceOutside(say('removed'), card.querySelector('[data-hom-measure-status]'));
    const add = card.querySelector('[data-hom-measure-add]');
    if (add) add.focus();
  };

  /* Card buttons */
  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-hom-measure-add], [data-hom-measure-edit], [data-hom-measure-remove]');
    if (!button) return;
    const card = button.closest('[data-hom-measure-card]');
    if (!card) return;
    event.preventDefault();
    if (button.hasAttribute('data-hom-measure-remove')) {
      removeFromForm(card);
      return;
    }
    const form = document.getElementById(card.dataset.formId);
    if (!form) return;
    setAlert(card, '');
    openForProduct(form, { opener: button }).catch(() => {});
  });

  /* Variant changes: show, hide or switch the card; keep the form's inputs in step. */
  HOM.onVariantChange(null, (variant, sectionId) => {
    document.querySelectorAll('[data-hom-measure-card]').forEach((card) => {
      if (card.dataset.homSection !== String(sectionId)) return;
      const state = HOM.fulfilmentState(variant.options, flagsOf(card));
      card.dataset.variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
      if (card.dataset.state !== state) {
        card.dataset.state = state;
        setAlert(card, '');
      }
      renderCard(card);
      syncInputs(card.dataset.formId);
    });
  });

  /* ---------- Add to bag guard (Custom Size needs saved measurements) ---------- */
  const isProductForm = (form) =>
    Boolean(form) && form.tagName === 'FORM' && /\/cart\/add/.test(form.getAttribute('action') || '');

  const blockingCard = (form) => {
    const main = mainFormFor(form);
    const formId = main && main.getAttribute('id');
    if (!formId) return null;
    const card = cardForForm(formId);
    if (!card || card.dataset.requireCustom !== 'true' || card.dataset.state !== 'custom') return null;
    return isComplete(entries.get(entryKey(formId, card)), true) ? null : { card, form: main };
  };

  const stopForMeasurements = (event, form, opener) => {
    const hit = blockingCard(form);
    if (!hit) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    setAlert(hit.card, hit.card.dataset.requiredMessage || say('required'));
    if (!session) openForProduct(hit.form, { required: true, opener }).catch(() => {});
    return true;
  };

  window.addEventListener(
    'click',
    (event) => {
      if (!enabled || event.button > 0) return;
      // Only the Add to bag buttons (Zenith's, the sticky bar's and Avis's clone of it), so app
      // buttons inside the form keep working; any other way of submitting meets the submit guard.
      const button =
        event.target.closest && event.target.closest('button[name="add"], .product-form__submit, input[type="submit"][name="add"]');
      if (!button) return;
      const form = button.form || button.closest('form');
      if (!isProductForm(form)) return;
      stopForMeasurements(event, form, button);
    },
    true
  );
  window.addEventListener(
    'submit',
    (event) => {
      if (!enabled) return;
      const form = event.target;
      if (!isProductForm(form)) return;
      stopForMeasurements(event, form, event.submitter || document.activeElement);
    },
    true
  );

  /* ---------- Bag lines ---------- */
  const route = (name, fallback) => (window.routes && window.routes[name]) || fallback;
  const cartJsUrl = () => `${route('cart_url', '/cart')}.js`;
  const changeJsUrl = () => `${route('cart_change_url', '/cart/change')}.js`;

  const fetchCart = async () => {
    const response = await fetch(cartJsUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`hom-measurements: cart ${response.status}`);
    return response.json();
  };

  const findLine = async (lineKey) => {
    const cart = await fetchCart();
    const items = cart.items || [];
    const index = items.findIndex((item) => item.key === lineKey);
    if (index < 0) throw new Error('hom-measurements: line not found');
    return { index, item: items[index] };
  };

  const changeLine = async (lineKey, quantity, properties) => {
    const response = await fetch(changeJsUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity, properties }),
    });
    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }
    if (!response.ok || !data || data.status) {
      throw new Error(`hom-measurements: ${(data && (data.description || data.message)) || response.status}`);
    }
    return data;
  };

  /** The line of this variant that passes check, looking at the same position first. */
  const pickLine = (items, index, variantId, check) => {
    const ordered = items[index] ? [items[index], ...items.filter((item, i) => i !== index)] : items;
    return ordered.find((item) => item.variant_id === variantId && check(item.properties || {})) || null;
  };

  /** Reads the bag again and returns the line that passed check. */
  const verifyLine = async (index, variantId, check) => {
    const cart = await fetchCart();
    const line = pickLine(cart.items || [], index, variantId, check);
    if (!line) throw new Error('hom-measurements: the bag did not keep the change');
    return line;
  };

  const cartChanged = (key, previousKey) => {
    document.dispatchEvent(new CustomEvent('hom:cart:changed', { detail: { key, previousKey } }));
  };

  const lineContext = (item) =>
    [item.product_title, item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '']
      .filter(Boolean)
      .join(' · ');

  // Shopify may tidy whitespace in property values; compare the text only.
  const sameValue = (a, b) =>
    String(a == null ? '' : a).replace(/\s+/g, ' ').trim() === String(b == null ? '' : b).replace(/\s+/g, ' ').trim();

  const openLine = async (lineKey, mode, options = {}) => {
    if (!enabled) return null;
    // Safari does not focus a clicked button, so callers may pass the opener themselves.
    const opener = options.opener || document.activeElement;
    const { index, item } = await findLine(lineKey);
    const props = item.properties || {};
    let values = null;
    let prefilled = false;
    if (mode === 'edit' && hasMeasurements(props)) values = valuesFromProps(props);
    if (!values) {
      const stored = loadStored();
      if (stored) {
        values = valuesFromStored(stored);
        prefilled = true;
      }
    }
    const custom = HOM.fulfilmentState(item.variant_options || [], {}) === 'custom';

    // A line's key includes its properties, so it changes with every save: a second Save (after a
    // failed check) goes to the key the line has now.
    let currentKey = lineKey;
    let saving = null; // the last save, so a form the browser closed mid-save still reports it
    let failure = null; // set when the form had to close because the line could not be followed

    const saveLine = async (newProps) => {
      const own = session;
      const properties = Object.assign(withoutMeasurementKeys(props), newProps);
      const check = (p) =>
        Object.keys(newProps).every((key) => sameValue(p[key], newProps[key])) &&
        !(REMOVED in p) &&
        !(REMOVED_NOTE_KEY in p);
      const changed = await changeLine(currentKey, item.quantity, properties);
      // The bag has changed. Follow the line (same position and variant) in the cart that
      // /cart/change.js returned, in case the check below fails and the customer saves again.
      const changedItems = (changed && changed.items) || [];
      const moved =
        pickLine(changedItems, index, item.variant_id, check) ||
        (changedItems[index] && changedItems[index].variant_id === item.variant_id ? changedItems[index] : null);
      if (moved) currentKey = moved.key;
      let line;
      try {
        line = await verifyLine(index, item.variant_id, check);
      } catch (error) {
        cartChanged(null, lineKey); // the bag did change: let it show what it holds now
        if (!moved) {
          // No key to save to again: close the form (focus returns) and reject.
          failure = error;
          if (session === own) close();
        }
        throw error;
      }
      return { key: line.key, previousKey: lineKey, properties: line.properties };
    };

    let result = await openDialog({
      mode: 'line',
      custom,
      values,
      prefilled,
      context: lineContext(item),
      opener,
      onSave: (formValues, newProps) => {
        saving = saveLine(newProps);
        return saving;
      },
    });
    if (!result && saving) {
      // Closed by the browser while the bag was being updated: wait for that update.
      result = await saving.catch(() => null);
    }
    if (failure) throw failure;
    // After the dialog has closed and focus is back on the opener (see the notes at the top).
    if (result) cartChanged(result.key, result.previousKey);
    return result;
  };

  const removeLine = async (lineKey) => {
    if (!enabled) return null;
    const { index, item } = await findLine(lineKey);
    const date = today();
    const properties = Object.assign(withoutMeasurementKeys(item.properties || {}), {
      [REMOVED_NOTE_KEY]: config.removedNote,
      [REMOVED]: date,
    });
    await changeLine(lineKey, item.quantity, properties);
    let line;
    try {
      line = await verifyLine(
        index,
        item.variant_id,
        (p) => p[REMOVED] === date && !MEASUREMENT_KEYS.some((key) => key in p)
      );
    } catch (error) {
      cartChanged(null, lineKey);
      throw error;
    }
    cartChanged(line.key, lineKey);
    announceOutside(say('removed'));
    return { key: line.key, previousKey: lineKey, properties: line.properties };
  };

  /* ---------- Quick view, section reloads, re-rendered markup ---------- */
  const placeInQuickView = (container) => {
    const card = container.querySelector('[data-hom-measure-card]');
    if (!card) return;
    const picker = container.querySelector('.product-parameters');
    if (picker && picker.nextElementSibling !== card) picker.after(card);
  };

  new MutationObserver((records) => {
    const cards = new Set();
    let quickView = null;
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches('[data-hom-measure-card]')) cards.add(node);
        else if (node.firstElementChild) node.querySelectorAll('[data-hom-measure-card]').forEach((card) => cards.add(card));
      });
      const target = record.target;
      if (record.addedNodes.length && target.nodeType === 1 && (target.getAttribute('id') || '').startsWith('QuickViewInfo-')) {
        quickView = target;
      }
    });
    cards.forEach(initCard);
    // After hom-pdp.js has put the delivery notice under the picker (its observer runs after this one).
    if (quickView) queueMicrotask(() => placeInQuickView(quickView));
  }).observe(document.body, { childList: true, subtree: true });

  // Quick view is emptied on close; forget what was saved there (its form id is shared by products).
  document.body.addEventListener('quick-view-closed', () => {
    Array.from(entries.keys()).forEach((key) => {
      if (key.includes('quickview-')) entries.delete(key);
    });
  });

  document.querySelectorAll('[data-hom-measure-card]').forEach(initCard);

  HOM.measurements = {
    enabled,
    keys: { visible: VISIBLE_KEYS.slice(), marker: MARKER, consent: CONSENT, removed: REMOVED, removedNote: REMOVED_NOTE_KEY },
    visibleKeys: VISIBLE_KEYS.slice(),
    hasMeasurements,
    openForProduct,
    editLine: (lineKey, options) => openLine(lineKey, 'edit', options),
    addToLine: (lineKey, options) => openLine(lineKey, 'add', options),
    removeLine,
  };
  // This file loads after hom-cart.js: other scripts read HOM.measurements when they need it
  // (render or click time), or wait for this event.
  document.dispatchEvent(new CustomEvent('hom:measurements:ready', { detail: { enabled } }));
})();
